import { create } from 'zustand';
import {
  queueControlApi,
  knowledgeFleetApi,
  type BudgetStatus,
  type FleetConfig,
  type LineageNode,
  type NviProjectionsResponse,
  type NviSnapshot,
  type NviSnapshotRecord,
  type QueueControlStatus,
  type QueueTask,
  type ScoringWeightHistoryEntry,
  type ScoringWeights,
} from '@/services/api';

interface FmqProgress {
  probed: number;
  unprobed: number;
  total: number;
}

export const DEFAULT_SCORING_WEIGHTS: ScoringWeights = {
  coverage: 0.25,
  density: 0.2,
  diversity: 0.2,
  leverage: 0.2,
  freshness: 0.15,
};

interface FleetState {
  status: QueueControlStatus | null;
  tasks: QueueTask[];
  budget: BudgetStatus | null;
  nvi: NviSnapshot | null;
  nviProjections: NviProjectionsResponse | null;
  nviSnapshots: NviSnapshotRecord[];
  recentRoots: LineageNode[];
  fleetConfig: FleetConfig | null;
  weightHistory: ScoringWeightHistoryEntry[];
  fmqProgress: FmqProgress | null;
  taskFilter: string;
  activeClient: string;
  loading: boolean;
  fleetConfigLoading: boolean;
  projectionsLoading: boolean;
  error: string | null;

  fetchAll: (clientId?: string) => Promise<void>;
  fetchTasks: (statusFilter?: string) => Promise<void>;
  setTaskFilter: (filter: string) => void;
  setActiveClient: (clientId: string) => void;
  triggerDispatch: () => Promise<number>;
  fetchFleetConfig: (clientId: string) => Promise<void>;
  updateFleetConfig: (config: Omit<FleetConfig, 'updated_at' | 'source'>) => Promise<void>;
  fetchFmqProgress: (clientId: string) => Promise<void>;
  fetchNviProjections: () => Promise<void>;
  fetchNviSnapshots: () => Promise<void>;
  fetchRecentRoots: (clientId?: string) => Promise<void>;
  fetchWeightHistory: (clientId: string) => Promise<void>;
  saveScoringWeights: (
    clientId: string,
    weights: ScoringWeights,
  ) => Promise<void>;
  resetScoringWeights: (clientId: string) => Promise<void>;
}

export const useFleetStore = create<FleetState>((set, get) => ({
  status: null,
  tasks: [],
  budget: null,
  nvi: null,
  nviProjections: null,
  nviSnapshots: [],
  recentRoots: [],
  fleetConfig: null,
  weightHistory: [],
  fmqProgress: null,
  taskFilter: 'all',
  activeClient: '',
  loading: false,
  fleetConfigLoading: false,
  projectionsLoading: false,
  error: null,

  fetchAll: async (clientId?: string) => {
    set({ loading: true, error: null });
    try {
      const [status, budget, nvi] = await Promise.allSettled([
        queueControlApi.getStatus(),
        queueControlApi.getBudgetStatus(),
        queueControlApi.getNviSnapshot(),
      ]);
      set({
        status: status.status === 'fulfilled' ? status.value : null,
        budget: budget.status === 'fulfilled' ? budget.value : null,
        nvi: nvi.status === 'fulfilled' ? nvi.value : null,
      });

      if (clientId) {
        await Promise.allSettled([
          get().fetchFleetConfig(clientId),
          get().fetchFmqProgress(clientId),
          get().fetchWeightHistory(clientId),
        ]);
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load fleet data' });
    } finally {
      set({ loading: false });
    }
  },

  fetchTasks: async (statusFilter?: string) => {
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const tasks = await queueControlApi.listTasks(filter);
      set({ tasks });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load tasks' });
    }
  },

  setTaskFilter: (filter: string) => {
    set({ taskFilter: filter });
    get().fetchTasks(filter);
  },

  setActiveClient: (clientId: string) => {
    set({ activeClient: clientId });
  },

  triggerDispatch: async () => {
    try {
      const res = await queueControlApi.triggerDispatch();
      get().fetchAll(get().activeClient || undefined);
      return res.dispatched;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Dispatch failed' });
      return 0;
    }
  },

  fetchFleetConfig: async (clientId: string) => {
    set({ fleetConfigLoading: true });
    try {
      const config = await queueControlApi.getFleetConfig(clientId);
      set({ fleetConfig: config });
    } catch (e) {
      console.warn('Fleet config fetch failed:', e);
    } finally {
      set({ fleetConfigLoading: false });
    }
  },

  updateFleetConfig: async (config) => {
    set({ fleetConfigLoading: true });
    try {
      const updated = await queueControlApi.updateFleetConfig(config);
      set({ fleetConfig: updated });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to update fleet config' });
    } finally {
      set({ fleetConfigLoading: false });
    }
  },

  fetchFmqProgress: async (clientId: string) => {
    try {
      const progress = await queueControlApi.getFmqSweepProgress(clientId);
      set({ fmqProgress: progress });
    } catch (e) {
      console.warn('FMQ progress fetch failed:', e);
    }
  },

  fetchNviProjections: async () => {
    set({ projectionsLoading: true });
    try {
      const projections = await queueControlApi.getNviProjections();
      set({ nviProjections: projections });
    } catch (e) {
      console.warn('NVI projections fetch failed:', e);
    } finally {
      set({ projectionsLoading: false });
    }
  },

  fetchNviSnapshots: async () => {
    try {
      const snapshots = await queueControlApi.listNviSnapshots(50);
      set({ nviSnapshots: snapshots });
    } catch (e) {
      console.warn('NVI snapshot list failed:', e);
    }
  },

  fetchRecentRoots: async (clientId?: string) => {
    try {
      const roots = await knowledgeFleetApi.listLineageRoots({
        client_id: clientId,
        limit: 20,
      });
      set({ recentRoots: roots });
    } catch (e) {
      console.warn('Lineage roots fetch failed:', e);
      set({ recentRoots: [] });
    }
  },

  fetchWeightHistory: async (clientId: string) => {
    try {
      const history = await queueControlApi.getWeightHistory(clientId, 10);
      set({ weightHistory: history });
    } catch (e) {
      console.warn('Weight history fetch failed:', e);
      set({ weightHistory: [] });
    }
  },

  saveScoringWeights: async (clientId: string, weights: ScoringWeights) => {
    const { fleetConfig } = get();
    // Use current config as base — if missing, pull defaults the backend would apply.
    const base = fleetConfig ?? {
      client_id: clientId,
      paused: false,
      max_agents_per_cycle: 10,
      daily_budget_usd: 10,
      min_task_priority: 0.3,
      max_probe_batches: 30,
      max_research_depth: 3,
    };
    await get().updateFleetConfig({
      ...base,
      client_id: clientId,
      scoring_weight_overrides: weights,
    });
    await get().fetchWeightHistory(clientId);
  },

  resetScoringWeights: async (clientId: string) => {
    const { fleetConfig } = get();
    const base = fleetConfig ?? {
      client_id: clientId,
      paused: false,
      max_agents_per_cycle: 10,
      daily_budget_usd: 10,
      min_task_priority: 0.3,
      max_probe_batches: 30,
      max_research_depth: 3,
    };
    await get().updateFleetConfig({
      ...base,
      client_id: clientId,
      scoring_weight_overrides: {},
    });
    await get().fetchWeightHistory(clientId);
  },
}));
